import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

export default function ImmigrationFilingManager() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [meta, setMeta] = useState({ officers: [] });
  const [assignRow, setAssignRow] = useState(null);
  const [officer, setOfficer] = useState("");

  const load = async () => {
    const [x, m] = await Promise.all([api.get("/immigration/manager-dashboard"), api.get("/immigration/meta")]);
    setD(x.data); setMeta(m.data);
  };
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;

  const doAssign = async () => {
    if (!officer) return toast.error("Pick officer");
    try {
      await api.post(`/immigration/applications/${assignRow.id}/assign`, { officer_id: officer });
      toast.success("Assigned"); setAssignRow(null); setOfficer(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const kpi = [
    ["Unassigned", d.unassigned_count, "text-rose-600"],
    ["Preparing", d.counts["Preparing"] || 0, "text-blue-600"],
    ["Under Review", d.counts["Under Review"] || 0, "text-indigo-600"],
    ["Ready to Submit", d.counts["Approved for Submission"] || 0, "text-emerald-600"],
    ["Submitted", d.counts["Submitted"] || 0, "text-teal-600"],
    ["Docs Requested", d.counts["Documents Requested"] || 0, "text-amber-600"],
    ["Approved", d.counts["Approved"] || 0, "text-green-600"],
    ["Refused", d.counts["Refused"] || 0, "text-red-600"],
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">Filing Manager Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">{d.total} total immigration files</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {kpi.map(([label, val, cls]) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-xs uppercase text-slate-500">{label}</div>
            <div className={`heading-font text-2xl font-bold mt-1 ${cls}`}>{val}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 heading-font font-semibold">Unassigned Queue ({d.unassigned.length})</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">ID</th>
              <th className="text-left px-4 py-2">Application Type</th>
              <th className="text-left px-4 py-2">Priority</th>
              <th className="text-left px-4 py-2">Salesperson</th>
              <th className="text-left px-4 py-2">Status Expiry</th>
              <th className="text-left px-4 py-2">Received</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {d.unassigned.map(a => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 mono text-xs text-orange-600 cursor-pointer" onClick={()=>nav(`/immigration/applications/${a.id}`)}>{a.application_number}</td>
                <td className="px-4 py-2">{a.app_type_name}</td>
                <td className="px-4 py-2">{a.priority}</td>
                <td className="px-4 py-2">{a.sales_name || "—"}</td>
                <td className="px-4 py-2">{fmtDate(a.status_expiry_date)}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{fmtDate(a.created_at)}</td>
                <td className="px-4 py-2 text-right">
                  <Button data-testid={`imm-fm-assign-${a.application_number}`} size="sm" onClick={()=>{ setAssignRow(a); setOfficer(""); }}><UserPlus className="w-4 h-4 mr-1"/>Assign</Button>
                </td>
              </tr>
            ))}
            {d.unassigned.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">Nothing waiting for assignment.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 heading-font font-semibold">Team Workload</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Officer</th>
              <th className="text-right px-4 py-2">Active</th>
              <th className="text-right px-4 py-2">Preparing</th>
              <th className="text-right px-4 py-2">Under Review / Changes</th>
              <th className="text-right px-4 py-2">Ready to Submit</th>
              <th className="text-right px-4 py-2">Submitted (Mo)</th>
              <th className="text-right px-4 py-2">Approved (Mo)</th>
            </tr>
          </thead>
          <tbody>
            {d.workload.map(w => (
              <tr key={w.officer_id} className="border-t border-slate-100">
                <td className="px-4 py-2 font-medium">{w.officer_name}</td>
                <td className="px-4 py-2 text-right">{w.active_files}</td>
                <td className="px-4 py-2 text-right">{w.preparing}</td>
                <td className="px-4 py-2 text-right">{w.waiting_changes}</td>
                <td className="px-4 py-2 text-right">{w.ready_to_submit}</td>
                <td className="px-4 py-2 text-right">{w.submitted_this_month}</td>
                <td className="px-4 py-2 text-right">{w.approved_this_month}</td>
              </tr>
            ))}
            {d.workload.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">No active filing officers.</td></tr>}
          </tbody>
        </table>
      </div>

      {assignRow && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-slate-200 p-6 w-full max-w-md space-y-4">
            <div className="heading-font text-lg font-semibold">Assign {assignRow.application_number}</div>
            <Select value={officer} onValueChange={setOfficer}>
              <SelectTrigger data-testid="imm-fm-officer-select"><SelectValue placeholder="Pick officer"/></SelectTrigger>
              <SelectContent>{meta.officers.map(o=><SelectItem key={o.user_id} value={o.user_id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={()=>setAssignRow(null)}>Cancel</Button>
              <Button data-testid="imm-fm-assign-save" onClick={doAssign} className="bg-orange-500 hover:bg-orange-600 text-white">Assign</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
